import Scrollhistorie from '@nrk/dh-basis-scrollhistorie'
import { closest } from '@nrk/dh-basis-common'
import { ScrollanimasjonMixin, parseOffsetString } from '@nrk/dh-basis-scrollanimasjon'
import scrollStyles from './media/shared/scroll-progress.module.css'
import altStyles from './cards/alt-text.module.css'

export class HistorieScroll extends ScrollanimasjonMixin(Scrollhistorie) {
  #scrollingTimeout

  connectedCallback() {
    super.connectedCallback()
    document.addEventListener('scroll', this.#handleScroll)
    this.addEventListener('focus', this.#handleFocus, true)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    document.removeEventListener('scroll', this.#handleScroll)
    this.removeEventListener('focus', this.#handleFocus, true)
  }

  #handleScroll = () => {
    if (this.#scrollingTimeout) {
      clearTimeout(this.#scrollingTimeout)
    } else {
      this.setAttribute('scrolling', '')
    }

    this.#scrollingTimeout = setTimeout(() => {
      this.removeAttribute('scrolling')
      this.#scrollingTimeout = null
    }, 300)
  }

  #handleFocus = (e) => {
    const backgroundElement = closest(e.target, '[data-background-id]')
    const altTextElement = e.target && closest(e.target, `.${altStyles.altText}`)
    let triggerElement
    if (backgroundElement) {
      const backgroundId = backgroundElement.getAttribute('data-background-id')
      if (this.activeBackgroundId !== backgroundId) {
        triggerElement = this.triggerElements.find(
          (elm) => elm.getAttribute('data-show-background') === backgroundId
        )
      }
    } else if (altTextElement) {
      triggerElement = altTextElement.nextElementSibling
    }

    if (triggerElement && triggerElement.getAttribute('data-state') !== 'ACTIVE') {
      triggerElement.scrollIntoView({ behavior: 'smooth' })
    }
  }

  /*
   * Handle scroll animation timeline update.
   */
  timelineUpdatedCallback(timelineId, progress) {
    const elements = this.querySelectorAll(
      `[data-background-id="${timelineId}"] .${scrollStyles.scrollProgress}`
    )
    for (let element of elements) {
      if (element instanceof HTMLElement) {
        element.style.setProperty('--dhhs-progress', progress.toFixed(2))
      }
    }
  }

  /*
   * Define scroll animation timelines.
   * Placeholder cards with no text generates an animation timeline that starts when the card
   * enters the screen and ends when the card leaves the screen. The offsets are defined on the
   * card itself using a `data-scroll-offsets` attribute.
   *
   * Scroll offsets are defined based on the scroll animations draft:
   * https://drafts.csswg.org/scroll-animations-1/#scroll-timeline-offset-section
   *
   * The offset target is set to the element with `data-scroll-offsets` attribute.
   *
   * Ex: `end 0.0, start 1.0`.
   */
  public get timelines() {
    return Array.from(this.querySelectorAll('[data-scroll-offsets]'))
      .map((element: HTMLElement) => ({
        id: element.getAttribute('data-scroll-timeline'),
        scrollOffsets: parseOffsetString(element.getAttribute('data-scroll-offsets'), element),
      }))
      .reduce(groupOffsetsByTimeline, [])
  }
}

function groupOffsetsByTimeline(list, { id, scrollOffsets }) {
  let timeline = list.find((el) => el.id === id)
  if (!timeline) {
    timeline = {
      id,
      scrollOffsets: [],
    }
    list.push(timeline)
  }
  timeline.scrollOffsets.push(...scrollOffsets)
  return list
}
